/**
 * ObservationChart - FHIR Observation Visualization
 *
 * Displays FHIR Observation data as charts (vital signs, lab results, etc.)
 *
 * FERP Principles:
 * - Data-driven: Chart from FHIR Observation resources
 * - Dumb frontend: Just visualizes, no business logic
 */
// @ts-nocheck


import React, { useMemo } from 'react';
import { Paper, Text, Stack, Group, Badge } from '@mantine/core';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export interface ObservationDataPoint {
  date: string;
  value: number;
  unit: string;
  status?: string;
}

export interface ObservationChartProps {
  /** Observation type (e.g., 'Blood Pressure', 'Heart Rate') */
  observationType: string;
  /** Data points */
  data: ObservationDataPoint[];
  /** Reference range */
  referenceRange?: {
    low?: number;
    high?: number;
  };
  /** Chart color */
  color?: string;
  /** Loading state */
  loading?: boolean;
}

export function ObservationChart({
  observationType,
  data,
  referenceRange,
  color = '#1976d2',
  loading = false,
}: ObservationChartProps): React.ReactElement {
  const chartData = useMemo(() => {
    return data.map((point) => ({
      date: new Date(point.date).toLocaleDateString(),
      value: point.value,
      unit: point.unit,
      refLow: referenceRange?.low,
      refHigh: referenceRange?.high,
    }));
  }, [data, referenceRange]);

  const latestValue = data[data.length - 1];

  if (loading) {
    return (
      <Paper p="md" withBorder>
        <Text c="dimmed">Loading {observationType}...</Text>
      </Paper>
    );
  }

  if (data.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Text c="dimmed">No {observationType} data available</Text>
      </Paper>
    );
  }

  const isOutOfRange =
    latestValue &&
    referenceRange &&
    (latestValue.value < (referenceRange.low ?? -Infinity) ||
      latestValue.value > (referenceRange.high ?? Infinity));

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Text fw={600} size="lg">
            {observationType}
          </Text>
          {latestValue && (
            <Group gap="xs">
              <Text size="xl" fw={700} c={isOutOfRange ? 'red' : 'blue'}>
                {latestValue.value} {latestValue.unit}
              </Text>
              {isOutOfRange && (
                <Badge color="red" size="sm">
                  Out of Range
                </Badge>
              )}
            </Group>
          )}
        </Group>

        {/* Reference Range */}
        {referenceRange && (
          <Text size="sm" c="dimmed">
            Normal Range: {referenceRange.low} - {referenceRange.high}{' '}
            {latestValue?.unit}
          </Text>
        )}

        {/* Chart */}
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            {referenceRange?.low && (
              <Line
                type="monotone"
                dataKey="refLow"
                stroke="#aaa"
                strokeDasharray="5 5"
                dot={false}
                name="Min"
              />
            )}
            {referenceRange?.high && (
              <Line
                type="monotone"
                dataKey="refHigh"
                stroke="#aaa"
                strokeDasharray="5 5"
                dot={false}
                name="Max"
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              name={observationType}
            />
          </LineChart>
        </ResponsiveContainer>
      </Stack>
    </Paper>
  );
}
